import {useMutation, UseMutationResult, useQuery, useQueryClient} from '@tanstack/react-query';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import {toast} from 'react-toastify';
import {TOOLTIP_DELAY} from '../../../constants';
import i18n from '../../../i18n';
import Button from '../../../incase-domain/components/button/Button';
import DropdownMenuCompact from '../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Modal from '../../../incase-domain/components/modals/directory/Modal';
import {IConnection} from '../../../incase-domain/connection';
import {IPresentation, SendOrShareType} from '../../../incase-domain/presentation';
import {IPaginatedResultWrapper} from '../../../incase-domain/search';
import {fetchOwnerSharedPresentationsPaginated, sendPresentations, sharePresentations} from '../../../service/presentation';
import {setConnectionListPageAction} from '../../../store/actions/connection.actions';
import {IUpdateError} from '../../../store/actions/types/error.types';
import {QUERY_KEYS} from '../../../utils/queryKeys';
import useButtonThrottle from '../../hooks/useButtonThrottle';
import Pagination from '../common/Pagination';
import SearchInput from '../common/SearchInput';
import TooltipWrapper from '../common/TooltipWrapper';
import LoadingSpinnerComponent from '../LoadingSpinnerComponent';

interface ISendSharePresentationModalProps {
    selected?: IPresentation[];
    setSelected?: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    sendPresentationModal: boolean;
    setSendPresentationModal?: React.Dispatch<React.SetStateAction<boolean>>;
    connections?: IPaginatedResultWrapper<IConnection>;
    onSearch?: (searchTerm: string) => void;
}

const buttonThrottleTime = 3000;

function SendSharePresentationModal(props: ISendSharePresentationModalProps): JSX.Element {
    const {selected, setSelected, sendPresentationModal, setSendPresentationModal, onSearch, connections} = props;
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [readOnlyState, setReadOnlyState] = useState<string[]>([]);
    const [sendOrShareState, setSendOrShareState] = useState<SendOrShareType>(SendOrShareType.SEND);
    const [isButtonThrottled, startThrottling] = useButtonThrottle();
    const queryClient = useQueryClient();

    const sendPresIds: string[] | undefined = selected?.map((selectedPres) => selectedPres._id);
    const singlePresId = selected?.length === 1 ? selected[0]._id : undefined;

    const {data: alreadySharedData} = useQuery(
        QUERY_KEYS.sharedPresentation.list({presentationId: singlePresId}),
        () => fetchOwnerSharedPresentationsPaginated(singlePresId || '', 1, 100),
        {
            enabled: !!singlePresId && sendOrShareState === SendOrShareType.SHARE,
            keepPreviousData: true,
        }
    );

    const alreadySharedEmails = new Set<string>(alreadySharedData?.content?.map((p) => p.userEmail as string) ?? []);
    const handleSharePresentations: UseMutationResult<
        {success: boolean},
        IUpdateError,
        {connectionUserId: string; sendPresIds: string[]; isReadOnly: boolean}
    > = useMutation(
        (variables: {connectionUserId: string; sendPresIds: string[]; isReadOnly: boolean}) =>
            sharePresentations(variables.connectionUserId, variables.sendPresIds, variables.isReadOnly),
        {
            onSuccess: () => {
                toast.success(i18n.t('editorPage.sharePresentations.sharePresentationModal.toastMessage.successfullyShared'));
                startThrottling(buttonThrottleTime);
                queryClient.invalidateQueries({queryKey: QUERY_KEYS.sharedPresentation.lists()});
            },
            onError: () => {
                toast.error(i18n.t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullyShared'));
            },
        }
    );

    const handleSendPresentations: UseMutationResult<{success: boolean}, IUpdateError, {userId: string; presentationIds: string[]}> =
        useMutation(
            (variables: {userId: string; presentationIds: string[]}) => sendPresentations(variables.userId, variables.presentationIds),
            {
                onSuccess: () => {
                    toast.success(i18n.t('editorPage.sharePresentations.sharePresentationModal.toastMessage.successfullySent'));
                    startThrottling(buttonThrottleTime);
                },
                onError: () => {
                    toast.error(i18n.t('editorPage.sharePresentations.sharePresentationModal.toastMessage.unsuccessfullySent'));
                },
            }
        );
    return (
        <Modal
            icon="solar:share-bold"
            header={t('editorPage.sharePresentations.sharePresentationModal.sharePresModalHeader.header')}
            description={
                selected && selected.length === 1
                    ? t('editorPage.sharePresentations.sharePresentationModal.sharePresModalHeader.onePresSelected')
                    : t('editorPage.sharePresentations.sharePresentationModal.sharePresModalHeader.multipleSelected')
            }
            isOpen={sendPresentationModal}
            onCancel={() => {
                setSendPresentationModal && setSendPresentationModal(false);
                setSelected && setSelected([]);
            }}
            onConfirm={() => {
                setSendPresentationModal && setSendPresentationModal(false);
                setSelected && setSelected([]);
                setSendOrShareState(SendOrShareType.SEND);
            }}
        >
            {connections?.content.length && connections?.content.length > 0 ? (
                <div className="connections">
                    <div className="u-pb-1">
                        {selected &&
                            selected.map((presentation) => (
                                <p key={presentation._id} className="u-pb-1">
                                    {presentation.name}
                                </p>
                            ))}
                    </div>
                    <div className="u-mb-1">
                        <DropdownMenuCompact
                            width="full"
                            items={[
                                [
                                    {
                                        label: t('editorPage.sharePresentations.sharePresentationModal.send'),
                                        onClick: () => setSendOrShareState(SendOrShareType.SEND),
                                    },
                                    {
                                        label: t('editorPage.sharePresentations.sharePresentationModal.share'),
                                        onClick: () => setSendOrShareState(SendOrShareType.SHARE),
                                    },
                                ],
                            ]}
                            className="c-sidepanel__dropdown"
                        >
                            <Button
                                type="button"
                                fullWidth
                                size="sm"
                                trailing="solar:alt-arrow-down-linear"
                                variant="soft"
                                className="c-sidepanel__dropdown_button"
                            >
                                {sendOrShareState === SendOrShareType.SEND
                                    ? t('editorPage.sharePresentations.sharePresentationModal.send')
                                    : t('editorPage.sharePresentations.sharePresentationModal.share')}
                            </Button>
                        </DropdownMenuCompact>
                    </div>
                    <SearchInput
                        placeholder={t('editorPage.sharePresentations.sharePresentationModal.searchConnections')}
                        onSearch={onSearch}
                    />
                    <div className="connections-list">
                        <div>
                            {connections?.isFetching ? (
                                <LoadingSpinnerComponent />
                            ) : (
                                connections?.content?.map((connection) => {
                                    const isAlreadyShared =
                                        sendOrShareState === SendOrShareType.SHARE && alreadySharedEmails.has(connection.email);

                                    return (
                                        <div className="connections-list-item" key={connection._id}>
                                            <p>{connection.email}</p>

                                            <div className="u-flex u-h-center">
                                                <TooltipWrapper
                                                    className="e-table__icon"
                                                    id="room-editRoom"
                                                    content={t('editorPage.readOnly')}
                                                    place="left"
                                                    delayShow={TOOLTIP_DELAY}
                                                >
                                                    {sendOrShareState === SendOrShareType.SHARE &&
                                                        !isAlreadyShared &&
                                                        (readOnlyState?.includes(connection._id) ? (
                                                            <Button
                                                                size="sm"
                                                                square
                                                                variant="ghost"
                                                                leading="solar:pen-2-broken"
                                                                onClick={() => {
                                                                    if (readOnlyState?.includes(connection._id)) {
                                                                        setReadOnlyState(
                                                                            readOnlyState.filter((readOnly) => readOnly !== connection._id)
                                                                        );
                                                                    } else {
                                                                        setReadOnlyState([...readOnlyState, connection._id]);
                                                                    }
                                                                }}
                                                            />
                                                        ) : (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                square
                                                                leading="solar:pen-2-linear"
                                                                onClick={() => {
                                                                    if (readOnlyState?.includes(connection._id)) {
                                                                        setReadOnlyState(
                                                                            readOnlyState.filter((readOnly) => readOnly !== connection._id)
                                                                        );
                                                                    } else {
                                                                        setReadOnlyState([...readOnlyState, connection._id]);
                                                                    }
                                                                }}
                                                            />
                                                        ))}
                                                </TooltipWrapper>
                                                <Button
                                                    variant="soft"
                                                    size="sm"
                                                    color="secondary"
                                                    onClick={() => {
                                                        sendOrShareState === SendOrShareType.SHARE
                                                            ? sendPresIds &&
                                                              handleSharePresentations.mutate({
                                                                  connectionUserId: connection.userId,
                                                                  sendPresIds,
                                                                  isReadOnly: readOnlyState.includes(connection._id),
                                                              })
                                                            : sendPresIds &&
                                                              handleSendPresentations.mutate({
                                                                  userId: connection.userId,
                                                                  presentationIds: sendPresIds,
                                                              });
                                                    }}
                                                    disabled={connections.isFetching || isButtonThrottled || isAlreadyShared}
                                                >
                                                    {isAlreadyShared
                                                        ? t('editorPage.sharePresentations.sharePresentationModal.alreadyShared')
                                                        : t('editorPage.sharePresentations.sharePresentationModal.sendPresentations')}
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                            {connections.numberOfElements > connections.pageSize && (
                                <div className="pagination-wrapper">
                                    <Pagination
                                        numberOfPages={Math.ceil(connections.numberOfElements / connections.pageSize)}
                                        page={connections.pageNumber}
                                        onPageClick={(pageNumber) => {
                                            dispatch(setConnectionListPageAction(pageNumber));
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div>{t('editorPage.sharePresentations.sharePresentationModal.noConnections')}</div>
            )}
        </Modal>
    );
}

export default SendSharePresentationModal;
